import { type LiterallyAnything } from '@/types/utils';

import { type Path, useI18n, type UseI18nOptions } from 'vue-i18n';
import { sanitizeHTML } from '@/utils/misc';

/**
 * A composable to more easily translate strings in component setup functions
 * or other composables.
 *
 * Usage example:
 * ```ts
 * const { t } = useTranslation();
 *
 * const myTranslationResult = t('translationKey');
 * ```
 */
export const useTranslation = (opts?: UseI18nOptions) => {
	const {
		t: translate,
		te: hasTranslation,
		setLocaleMessage,
	} = useI18n(opts);

	/**
	 * A small wrapper around the `vue-i18n` `$t` function so that it can more
	 * easily be used with TypeScript.
	 */
	const t = (key: Path, values?: LiterallyAnything) => {
		return String(translate(key, values));
	};

	/**
	 * Translate and sanitize the given langvar, but allow some elements that
	 * are common in langvars. This is useful for using with `v-html`.
	 *
	 * The list of allowed tags has been provided by Product Content, those are
	 * the tags that Lokalise supports and outputs.
	 *
	 * Note: Do be careful with adding allowed elements here!
	 */
	const sanitizedT = (key: Path, values?: LiterallyAnything) => {
		return sanitizeHTML(
			translate(key, values),
			[
				'a',
				'b',
				'i',
				'em',
				'strong',
				'br',
				'p',
				'h3',
				'q',
				'span',
				'div',
				'img',
				'ul',
				'ol',
				'li',
			],
			{
				allowedAttributes: {
					a: ['href', 'target'],
					img: ['src'],
				},
			}
		);
	};

	return { t, sanitizedT, setLocaleMessage, hasTranslation };
};
